import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';

export interface CheckboxProps extends React.HTMLAttributes<HTMLInputElement> {
  checked?: boolean;
  darkMode?: boolean;
  disabled?: boolean;
  disabledStylesUnchanged?: boolean;
  error?: string;
  iconStyle?: React.CSSProperties;
  id?: string;
  indeterminate?: boolean;
  inputRef?: any;
  label?: string | React.ReactNode;
  labelHelpText?: string;
  labelProps?: any;
  labelTooltip?: string;
  labelTooltipShowIcon?: boolean;
  size?: string;
  stopPropagation?: boolean;
  style?: React.CSSProperties;
  value?: any;
}

const CheckboxInput = ({
  darkMode,
  disabledStylesUnchanged,
  error,
  iconStyle,
  id,
  indeterminate,
  inputRef,
  label,
  labelHelpText,
  labelProps,
  labelTooltip,
  labelTooltipShowIcon = true,
  size = 'medium',
  stopPropagation,
  style,
  ...params
}: CheckboxProps) => {
  const { theme } = useReduxState();

  const handleClick = (ev) => {
    ev.stopPropagation();
  };

  const handleLabelMouseDown = (ev: React.MouseEvent<HTMLLabelElement>) => {
    // Prevent blur from interfering with checkbox toggle in React 19
    // This fixes the issue where clicking a label requires two clicks
    if (!params.disabled) {
      ev.preventDefault();
    }
  };

  let color = 'rgba(0, 0, 0, 0.1)';
  let iconColor = theme.successTextColor;
  let activeColor = theme.successColor;
  let outlineColor = 'rgba(0, 0, 0, 0.15)';
  let labelColor = theme.textColorLight;
  if (darkMode) {
    color = 'rgba(255, 255, 255, 0.5)';
    iconColor = theme.successTextColorDark;
    activeColor = theme.successColorDark;
    outlineColor = 'rgba(255, 255, 255, 0.2)';
    labelColor = '#fff';
  }

  let iconFontSize = '0.8rem';
  let iconPaddingTop = '4px';
  let iconSize = '18px';
  let iconTop = '1px';
  let labelFontSize = '1.5rem';

  if (size === 'small') {
    labelFontSize = '1.4rem';
  } else if (size === 'large') {
    iconFontSize = '1.1rem';
    iconPaddingTop = '3px';
    iconSize = '20px';
    iconTop = '3px';
    labelFontSize = '1.8rem';
  } else if (size === 'huge') {
    iconFontSize = '1.3rem';
    iconPaddingTop = '3px';
    iconSize = '24px';
    iconTop = '4px';
    labelFontSize = '2.2rem';
  }

  return (
    <div
      className={`checkbox ${indeterminate ? 'indeterminate' : ''}`}
      style={style}
      {...(stopPropagation ? { onClick: handleClick } : {})}
    >
      <input
        className="input"
        id={id}
        ref={inputRef}
        type="checkbox"
        {...params}
      />

      <label
        className={`label ${
          disabledStylesUnchanged ? '' : 'label-styles-changed'
        }`}
        htmlFor={id}
        onMouseDown={handleLabelMouseDown}
        {...labelProps}
      >
        <div className="icon" style={iconStyle}>
          <Icon icon={indeterminate ? 'minus' : 'check'} />
        </div>

        {label ? (
          <div
            className="text"
            {...(labelTooltip
              ? { 'aria-label': labelTooltip, 'data-tooltip': labelTooltip }
              : {})}
          >
            {label}

            {labelTooltip && labelTooltipShowIcon ? (
              <Icon
                icon="question-circle"
                style={{
                  fontSize: '1.4rem',
                  marginLeft: 5
                }}
              />
            ) : null}

            {labelHelpText ? (
              <div className="help-text">{labelHelpText}</div>
            ) : null}
          </div>
        ) : null}
      </label>

      {error ? <div className="error-message">{error}</div> : null}

      <style jsx>{`
        .checkbox {
          line-height: normal;
          position: relative;
        }
        .input {
          opacity: 0;
          overflow: hidden;
          position: absolute;
          height: 100%;
          width: max-content;
          top: 0;
          left: 0;
          margin: 0;
          padding: 0;
          z-index: 1;
          cursor: pointer;
        }
        .input + .label {
          align-items: flex-start;
          color: ${labelColor};
          cursor: pointer;
          display: flex;
          font-size: ${labelFontSize};
          font-weight: 400;
          position: relative;
          white-space: normal;
          width: auto;
          word-wrap: break-word;
        }
        .input + .label .icon {
          background-color: ${color};
          border: 0;
          border-radius: 5px;
          box-shadow: none;
          color: transparent;
          display: inline;
          flex-shrink: 0;
          font-size: ${iconFontSize};
          font-weight: 400;
          padding-top: ${iconPaddingTop};
          position: relative;
          pointer-events: none;
          text-align: center;
          top: ${iconTop};
          transition: background-color 0.1s, color 0.1s;
          height: ${iconSize};
          width: ${iconSize};
        }
        .input + .label .text {
          margin-left: 5px;
        }
        .input + .label:hover .icon {
          color: rgba(0, 0, 0, 0.2);
        }
        .input:checked + .label .icon {
          background-color: ${activeColor};
          color: ${iconColor};
        }
        .input:disabled + .label.label-styles-changed {
          cursor: default;
          opacity: 0.5;
        }
        .input:disabled + .label:hover .icon {
          color: transparent;
        }
        .input:disabled:checked + .label:hover .icon {
          color: ${iconColor};
        }
        .input:focus + .label .icon,
        .input:focus:checked + .label .icon {
          box-shadow: 0 0 0 5px ${outlineColor};
        }
        .help-text {
          color: ${theme.textColorLighter};
          font-size: 0.9em;
          margin-top: 3px;
        }
        .error-message {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          font-weight: 400;
          padding: 3px;
        }
      `}</style>
    </div>
  );
};

export default CheckboxInput;
